export interface BlogPostMeta {
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  image: string;
  metaDescription?: string;
}

export interface BlogPostContent {
  meta: BlogPostMeta;
  content: string[];
}

// Simple frontmatter parser (no dependencies)
const parseFrontmatter = (markdown: string) => {
  const lines = markdown.split('\n');
  
  if (lines[0] !== '---') {
    return { data: {}, content: markdown };
  }
  
  let endIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === '---') {
      endIndex = i;
      break;
    }
  }
  
  if (endIndex === -1) {
    return { data: {}, content: markdown };
  }
  
  const frontmatterLines = lines.slice(1, endIndex);
  const content = lines.slice(endIndex + 1).join('\n');
  
  const data: any = {};
  for (const line of frontmatterLines) {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.slice(0, colonIndex).trim();
      let value = line.slice(colonIndex + 1).trim();
      
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      data[key] = value;
    }
  }
  
  return { data, content };
};

export const loadBlogPost = async (slug: string, language: string): Promise<BlogPostContent | null> => {
  try {
    const response = await fetch(`/content/${language}/${slug}.md`);
    if (!response.ok) {
      throw new Error('Post not found');
    }
    
    const markdown = await response.text();
    const { data, content } = parseFrontmatter(markdown);
    
    // Split content into paragraphs
    const paragraphs = content
      .split('\n\n')
      .filter(p => p.trim() !== '')
      .map(p => p.trim());
    
    return {
      meta: data as BlogPostMeta,
      content: paragraphs
    };
  } catch (error) {
    console.error('Error loading blog post:', error);
    return null;
  }
};